import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send } from 'lucide-react';
import colors from '../theme/colors';
import { contactInfo } from '../data/mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from '../hooks/use-toast';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: 'henna',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock form submission
    toast({
      title: "Message Sent!",
      description: "Thank you for contacting us. We'll get back to you soon.",
    });
    setFormData({ name: '', email: '', phone: '', service: 'henna', message: '' });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div style={{ backgroundColor: colors.background }} className="min-h-screen">
      {/* Header Section */}
      <section style={{ background: `linear-gradient(135deg, ${colors.white} 0%, ${colors.background} 100%)` }} className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 style={{ color: colors.primary }} className="text-5xl md:text-6xl font-bold mb-6">Get in Touch</h1>
          <p style={{ color: colors.text.medium }} className="text-xl max-w-3xl mx-auto leading-relaxed">
            Have questions or ready to book? We'd love to hear from you!
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div>
            <h2 style={{ color: colors.primary }} className="text-3xl font-bold mb-8">Contact Information</h2>
            
            <div className="space-y-6 mb-10">
              <div style={{ backgroundColor: colors.white }} className="flex items-start space-x-4 p-6 rounded-xl shadow-md">
                <div style={{ backgroundColor: colors.highlight }} className="p-3 rounded-full">
                  <Phone style={{ color: colors.white }} size={24} />
                </div>
                <div>
                  <h3 style={{ color: colors.text.dark }} className="font-semibold text-lg mb-1">Phone</h3>
                  <a href={`tel:${contactInfo.phone}`} style={{ color: colors.text.medium }} className="hover:opacity-70 transition-opacity">
                    {contactInfo.phone}
                  </a>
                </div>
              </div>

              <div style={{ backgroundColor: colors.white }} className="flex items-start space-x-4 p-6 rounded-xl shadow-md">
                <div style={{ backgroundColor: colors.highlight }} className="p-3 rounded-full">
                  <Mail style={{ color: colors.white }} size={24} />
                </div>
                <div>
                  <h3 style={{ color: colors.text.dark }} className="font-semibold text-lg mb-1">Email</h3>
                  <a href={`mailto:${contactInfo.email}`} style={{ color: colors.text.medium }} className="hover:opacity-70 transition-opacity break-all">
                    {contactInfo.email}
                  </a>
                </div>
              </div>

              <div style={{ backgroundColor: colors.white }} className="flex items-start space-x-4 p-6 rounded-xl shadow-md">
                <div style={{ backgroundColor: colors.highlight }} className="p-3 rounded-full">
                  <MapPin style={{ color: colors.white }} size={24} />
                </div>
                <div>
                  <h3 style={{ color: colors.text.dark }} className="font-semibold text-lg mb-1">Location</h3>
                  <p style={{ color: colors.text.medium }}>Serving clients across the region</p>
                </div>
              </div>
            </div>

            <div 
              style={{ 
                background: `linear-gradient(135deg, ${colors.secondary} 0%, ${colors.highlight} 100%)`,
                color: colors.white 
              }}
              className="rounded-2xl p-8"
            >
              <h3 className="text-2xl font-bold mb-4">Business Hours</h3>
              <div className="space-y-2">
                <p className="text-base">Monday - Saturday: 10:00 AM - 7:00 PM</p>
                <p className="text-base">Sunday: By Appointment Only</p>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <div style={{ backgroundColor: colors.white }} className="rounded-2xl p-8 shadow-lg">
              <h2 style={{ color: colors.primary }} className="text-3xl font-bold mb-6">Send Us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label style={{ color: colors.text.dark }} className="block text-sm font-semibold mb-2">Name *</label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Your full name"
                    className="w-full"
                    style={{ borderColor: colors.accent }}
                  />
                </div>

                <div>
                  <label style={{ color: colors.text.dark }} className="block text-sm font-semibold mb-2">Email *</label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="your.email@example.com"
                    className="w-full"
                    style={{ borderColor: colors.accent }}
                  />
                </div>

                <div>
                  <label style={{ color: colors.text.dark }} className="block text-sm font-semibold mb-2">Phone *</label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="+91 98765 43210"
                    className="w-full"
                    style={{ borderColor: colors.accent }}
                  />
                </div>

                <div>
                  <label style={{ color: colors.text.dark }} className="block text-sm font-semibold mb-2">Service Interested In *</label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                    style={{ borderColor: colors.accent, color: colors.text.dark }}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-50"
                  >
                    <option value="henna">Henna Art</option>
                    <option value="nails">Nail Art</option>
                    <option value="both">Both Services</option>
                    <option value="cones">Henna Cones</option>
                  </select>
                </div>

                <div>
                  <label style={{ color: colors.text.dark }} className="block text-sm font-semibold mb-2">Message *</label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder="Tell us about your requirements..."
                    className="w-full min-h-[120px]"
                    style={{ borderColor: colors.accent }}
                  />
                </div>

                <Button
                  type="submit"
                  style={{ backgroundColor: colors.highlight, color: colors.white }}
                  className="w-full py-6 text-lg font-semibold rounded-full transition-all duration-300 hover:opacity-90 flex items-center justify-center space-x-2"
                >
                  <Send size={20} />
                  <span>Send Message</span>
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;